/**
 * Authentication Context for Fantomize
 *
 * Provides authentication state and methods throughout the app.
 * Handles sign up, login, logout, and token management.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../config/firebase';

const AuthContext = createContext();

/**
 * Hook to access authentication context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Authentication Provider Component
 *
 * Features:
 * - 15-day session tokens
 * - Auto-refresh every hour
 * - Persistent sessions (survives browser close)
 * - Auto-logout after 15 days of inactivity
 */
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionExpiry, setSessionExpiry] = useState(null);

  // Session configuration
  const SESSION_DURATION = 15 * 24 * 60 * 60 * 1000; // 15 days in milliseconds
  const REFRESH_INTERVAL = 60 * 60 * 1000; // Refresh token every hour
  const SESSION_STORAGE_KEY = 'fantomize_session_expiry';

  // Check if Firebase is configured
  const isFirebaseAvailable = auth !== null;

  // If Firebase is not available, immediately set loading to false
  useEffect(() => {
    if (!isFirebaseAvailable) {
      console.warn('⚠️ Firebase not configured. Auth features disabled.');
      setLoading(false);
      return;
    }
  }, [isFirebaseAvailable]);

  /**
   * Update session expiry time
   */
  const updateSessionExpiry = () => {
    const expiryTime = Date.now() + SESSION_DURATION;
    setSessionExpiry(expiryTime);
    localStorage.setItem(SESSION_STORAGE_KEY, expiryTime.toString());
    console.log('[AuthContext] Session extended to:', new Date(expiryTime).toLocaleTimeString());
  };

  /**
   * Check if session is still valid
   */
  const isSessionValid = () => {
    const storedExpiry = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!storedExpiry) return false;

    const expiryTime = parseInt(storedExpiry);
    const isValid = Date.now() < expiryTime;

    if (!isValid) {
      console.log('[AuthContext] Session expired');
    }

    return isValid;
  };

  /**
   * Clear session data
   */
  const clearSession = () => {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    setSessionExpiry(null);
    console.log('[AuthContext] Session cleared');
  };

  /**
   * Sign up with email and password
   */
  async function signup(email, password) {
    if (!isFirebaseAvailable) {
      throw new Error('Firebase authentication is not configured');
    }
    try {
      setError(null);
      const result = await createUserWithEmailAndPassword(auth, email, password);
      updateSessionExpiry();
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  /**
   * Log in with email and password
   */
  async function login(email, password) {
    if (!isFirebaseAvailable) {
      throw new Error('Firebase authentication is not configured');
    }
    try {
      setError(null);
      const result = await signInWithEmailAndPassword(auth, email, password);
      updateSessionExpiry();
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  /**
   * Log in with Google
   */
  async function loginWithGoogle() {
    if (!isFirebaseAvailable) {
      console.error('[AuthContext] Firebase not available');
      throw new Error('Firebase authentication is not configured');
    }
    console.log('[AuthContext] Starting Google login...');
    try {
      setError(null);
      const provider = new GoogleAuthProvider();
      console.log('[AuthContext] Opening Google popup...');
      const result = await signInWithPopup(auth, provider);
      console.log('[AuthContext] Google login successful:', {
        uid: result.user.uid,
        email: result.user.email
      });

      // Set 15-day session
      updateSessionExpiry();

      return result;
    } catch (err) {
      console.error('[AuthContext] Google login error:', {
        code: err.code,
        message: err.message
      });
      setError(err.message);
      throw err;
    }
  }

  /**
   * Log out current user
   */
  async function logout() {
    if (!isFirebaseAvailable) {
      throw new Error('Firebase authentication is not configured');
    }
    try {
      setError(null);
      clearSession();
      return await signOut(auth);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  /**
   * Send password reset email
   */
  async function resetPassword(email) {
    if (!isFirebaseAvailable) {
      throw new Error('Firebase authentication is not configured');
    }
    try {
      setError(null);
      return await sendPasswordResetEmail(auth, email);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  /**
   * Get Firebase ID token for API calls
   * This token is sent to your backend for authentication
   */
  async function getIdToken(forceRefresh = false) {
    if (currentUser) {
      try {
        return await currentUser.getIdToken(forceRefresh);
      } catch (err) {
        console.error('Failed to get ID token:', err);
        return null;
      }
    }
    return null;
  }

  /**
   * Listen for authentication state changes
   * Includes 15-day session management
   */
  useEffect(() => {
    if (!isFirebaseAvailable) {
      // Firebase not configured, skip auth state listener
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // User is signed in
        const storedExpiry = localStorage.getItem(SESSION_STORAGE_KEY);

        if (!storedExpiry) {
          // First time login - create session
          console.log('[AuthContext] First login, creating session');
          setCurrentUser(user);
          updateSessionExpiry();
        } else if (isSessionValid()) {
          // Existing valid session
          console.log('[AuthContext] User authenticated, session valid');
          setCurrentUser(user);
          updateSessionExpiry(); // Refresh session on activity
        } else {
          // Session expired, log out
          console.log('[AuthContext] Session expired, logging out');
          signOut(auth);
          clearSession();
          setCurrentUser(null);
        }
      } else {
        // User is signed out
        console.log('[AuthContext] User signed out');
        setCurrentUser(null);
        clearSession();
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [isFirebaseAvailable]);

  /**
   * Auto-refresh token every hour while user is active
   * and session is valid
   */
  useEffect(() => {
    if (!currentUser || !isFirebaseAvailable) {
      return;
    }

    console.log('[AuthContext] Starting token refresh interval');

    const refreshInterval = setInterval(async () => {
      if (!isSessionValid()) {
        console.log('[AuthContext] Session expired during refresh check, logging out');
        await signOut(auth);
        clearSession();
        return;
      }

      try {
        console.log('[AuthContext] Refreshing ID token...');
        await currentUser.getIdToken(true); // Force refresh
        updateSessionExpiry(); // Extend session
        console.log('[AuthContext] Token refreshed successfully');
      } catch (err) {
        console.error('[AuthContext] Token refresh failed:', err);
      }
    }, REFRESH_INTERVAL);

    return () => {
      console.log('[AuthContext] Clearing token refresh interval');
      clearInterval(refreshInterval);
    };
  }, [currentUser, isFirebaseAvailable]);

  /**
   * Check session expiry every minute
   */
  useEffect(() => {
    if (!currentUser || !isFirebaseAvailable) {
      return;
    }

    const expiryCheckInterval = setInterval(() => {
      if (!isSessionValid()) {
        console.log('[AuthContext] Session expired, auto-logout');
        signOut(auth);
        clearSession();
      }
    }, 60 * 1000); // Check every minute

    return () => clearInterval(expiryCheckInterval);
  }, [currentUser, isFirebaseAvailable]);

  const value = {
    currentUser,
    loading,
    error,
    signup,
    login,
    loginWithGoogle,
    logout,
    resetPassword,
    getIdToken,
    sessionExpiry,
    isSessionValid,
    updateSessionExpiry
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}


